import React from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton
} from '@mui/material';
import {
    Add as AddIcon,
    Close as CloseIcon,
    RestartAlt as ResetIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import { RTL } from '../../cache/CacheRtl';
import { useMessageContext } from '../../contexts/MessageContext';
import { useAuth } from '../../contexts/AuthContext';
import { resetToOriginal } from '../../services/ExcersiceService';
import { getErrorMessage } from '../../enums/ErrorMessages';
import { useExerciseForm } from '../../hooks/useExerciseForm';
import ExerciseForm from './ExerciseForm';
import GroupAdminCompaniesField from '../groupadmin/GroupAdminCompaniesField';

// Helper shown under the group-admin company picker: the exercise is shared to the chosen authorized
// companies (like a workout program); leaving it empty keeps it private to the group admin's library.
const EXERCISE_COMPANIES_HELPER =
    'אופציונלי — התרגיל ישותף לחברות שתבחר; ניתן להשאיר ריק ולשמור אותו בספרייה שלך בלבד, ולשייך חברות בהמשך בעריכה';

/**
 * ExerciseDialog - Dialog for creating and editing exercises
 * Uses ExerciseForm component and useExerciseForm hook
 */
const ExerciseDialog = ({ isOpen, close, exercise, setReload }) => {
    const { notificationHandler } = useMessageContext();
    const { isCompanyGroupAdmin } = useAuth();
    const isGroupAdmin = isCompanyGroupAdmin();
    const isEditMode = !!exercise;
    const [isResetting, setIsResetting] = React.useState(false);

    // Use the shared form hook
    const form = useExerciseForm(exercise, {
        onSaveSuccess: () => {
            notificationHandler.success('תרגיל נשמר בהצלחה');
            setReload();
            handleClose();
        },
        onSaveError: (error) => {
            notificationHandler.error(
                getErrorMessage(error.response?.data?.errorCode) || 'שגיאה בשמירת התרגיל'
            );
        }
    });

    const handleClose = () => {
        form.resetForm();
        close();
    };

    const handleReset = () => {
        if (!window.confirm('האם אתה בטוח שברצונך לאפס את התרגיל למקור? פעולה זו תמחק את ההתאמות האישיות.')) {
            return;
        }

        setIsResetting(true);
        resetToOriginal(exercise.id)
            .then(() => {
                notificationHandler.success('התרגיל אופס למקור בהצלחה');
                setReload();
                handleClose();
            })
            .catch(e => {
                console.log(e);
                notificationHandler.error(
                    getErrorMessage(e.response?.data?.errorCode) || 'שגיאה באיפוס התרגיל'
                );
            })
            .finally(() => {
                setIsResetting(false);
            });
    };

    const handleSubmit = async () => {
        const result = await form.handleSave();
        if (!result.success && result.error === 'Validation failed') {
            notificationHandler.error('אנא מלא את כל השדות הנדרשים');
        } else if (!result.success && result.error === 'Uploads in progress') {
            notificationHandler.error('יש להמתין לסיום העלאת הקבצים');
        }
    };

    const canSubmit = form.isValid &&
        !form.nameExistsError &&
        !form.nameHebExistsError &&
        !form.isCheckingName &&
        !form.isCheckingNameHeb &&
        !form.isUploading;

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '12px',
                    direction: 'rtl',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: '#FCFCFD',
                    borderBottom: '1px solid #E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 600,
                            fontSize: '18px',
                            color: '#1E293B',
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        {isEditMode ? 'עריכת תרגיל' : 'יצירת תרגיל חדש'}
                    </Box>
                    {exercise?.variant && (
                        <Chip
                            label="גרסה מותאמת"
                            size="small"
                            sx={{
                                height: '24px',
                                fontSize: '12px',
                                fontWeight: 500,
                                backgroundColor: '#EFF6FF',
                                color: '#1E40AF',
                                border: '1px solid #BFDBFE'
                            }}
                        />
                    )}
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{ color: '#64748B' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    backgroundColor: '#FFFFFF',
                    padding: '24px',
                    direction: 'rtl',
                    overflow: 'auto'
                }}
            >
                <ExerciseForm
                    formValues={form.formValues}
                    setFieldValue={form.setFieldValue}
                    setFieldTouched={form.setFieldTouched}
                    uploadStates={form.uploadStates}
                    isUploading={form.isUploading}
                    imagePreview={form.imagePreview}
                    coverPreview={form.coverPreview}
                    isVideoUploadMode={form.isVideoUploadMode}
                    setIsVideoUploadMode={form.setIsVideoUploadMode}
                    videoFile={form.videoFile}
                    videoFileName={form.videoFileName}
                    videoPreviewUrl={form.videoPreviewUrl}
                    existingVideoUrl={form.existingVideoUrl}
                    handleImageUpload={form.handleImageUpload}
                    handleCoverExtracted={form.handleCoverExtracted}
                    handleVideoFileChange={form.handleVideoFileChange}
                    handleVideoUrlChange={form.handleVideoUrlChange}
                    handleRemoveVideo={form.handleRemoveVideo}
                    errors={form.errors}
                    touched={form.touched}
                    nameExistsError={form.nameExistsError}
                    nameHebExistsError={form.nameHebExistsError}
                    isCheckingName={form.isCheckingName}
                    isCheckingNameHeb={form.isCheckingNameHeb}
                    checkNameExists={form.checkNameExists}
                    checkNameHebExists={form.checkNameHebExists}
                    exercise={exercise}
                    mode="dialog"
                />

                {/* Company Group Administrators choose which of their authorized companies get this
                    exercise (a subset, like a workout program). Not shown to system/company admins. */}
                {isGroupAdmin && (
                    <Box sx={{ mt: 3 }}>
                        <GroupAdminCompaniesField
                            value={form.formValues.companyPermissions}
                            onChange={(companies) => form.setFieldValue('companyPermissions', companies)}
                            emptyHelperText={EXERCISE_COMPANIES_HELPER}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    backgroundColor: '#FCFCFD',
                    borderTop: '1px solid #E2E8F0',
                    padding: '16px 24px',
                    direction: 'rtl',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
            >
                <Box>
                    {exercise?.variant && (
                        <RTL>
                            <Button
                                onClick={handleReset}
                                variant="outlined"
                                startIcon={<ResetIcon />}
                                disabled={isResetting}
                                sx={{
                                    color: '#DC2626',
                                    borderColor: '#FCA5A5',
                                    fontFamily: '"Heebo", "Roboto", sans-serif',
                                    '&:hover': {
                                        borderColor: '#DC2626',
                                        backgroundColor: '#FEF2F2'
                                    }
                                }}
                            >
                                {isResetting ? 'מאפס...' : 'איפוס למקור'}
                            </Button>
                        </RTL>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={handleClose}
                        sx={{
                            color: 'secondary',
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        ביטול
                    </Button>
                    <RTL>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            startIcon={isEditMode ? <SaveIcon /> : <AddIcon />}
                            disabled={!canSubmit || form.isSaving}
                            sx={{
                                backgroundColor: '#57837E',
                                color: '#FFFFFF',
                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                '&:hover': {
                                    backgroundColor: '#466b67'
                                },
                                '&:disabled': {
                                    backgroundColor: '#CBD5E1'
                                }
                            }}
                        >
                            {form.isUploading ? 'מעלה קבצים...' :
                                form.isSaving ? 'שומר...' :
                                    (isEditMode ? 'שמירת שינויים' : 'יצירת תרגיל')}
                        </Button>
                    </RTL>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default ExerciseDialog;
